/**
 * BottomNav (PH9-4) — fixed bottom-edge nav extracted from Layout.tsx.
 *
 * Pinned to bottom with safe-area padding, equal-grid columns, accent
 * tone on active route via NavLink's isActive render-prop. Optional
 * per-tab badge counter (used today for unread chat count). Optional
 * haptic feedback on tap when window.navigator.vibrate is available.
 *
 * Кегль ярлыка — 11px (было 9px). Замер на проде 2026-08-07: при слоте
 * 78px (390px ширины, пять колонок) самый широкий ярлык из четырёх
 * пусковых локалей — «Analysis» и «Сьогодні» — занимает на 11px ровно
 * 59.8px, то есть 18px запаса. Трекинг 0.08em сохранён: место позволяет,
 * и «приборный» моноширинный вид от правки не страдает. 9px были ниже
 * платформенных ориентиров (таб-бар iOS 11pt, Material 12sp), и запас
 * ширины показал, что это не вынужденная мера, а просто мелко.
 */
import type { ReactElement } from "react";
import { NavLink } from "react-router-dom";

export interface BottomNavTab {
  to: string;
  label: string;
  icon: (props: { active?: boolean }) => ReactElement;
  /** Optional numeric badge — only rendered when > 0. */
  badgeCount?: number;
  /** Optional aria-label for the badge (e.g. localised "5 unread"). */
  badgeAriaLabel?: string;
  /** Optional testId for E2E pins. */
  testId?: string;
}

export interface BottomNavProps {
  tabs: BottomNavTab[];
  /** Fire navigator.vibrate(10) on tap if available. Default true. */
  haptic?: boolean;
  /** Optional testId on the <nav>. Default "bottom-nav". */
  testId?: string;
}

export function BottomNav({ tabs, haptic = true, testId = "bottom-nav" }: BottomNavProps) {
  const tap = () => {
    if (!haptic) return;
    try {
      if (typeof navigator !== "undefined" && "vibrate" in navigator) {
        navigator.vibrate(8);
      }
    } catch {
      // silent: vibrate API may be disabled or absent
    }
  };

  return (
    <nav
      data-testid={testId}
      className="fixed bottom-0 inset-x-0 border-t border-bg-divider bg-bg-base/95 backdrop-blur supports-[backdrop-filter]:bg-bg-base/85 grid z-30"
      style={{
        gridTemplateColumns: `repeat(${tabs.length}, 1fr)`,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {tabs.map((t) => {
        const Icon = t.icon;
        const showBadge = (t.badgeCount ?? 0) > 0;
        return (
          <NavLink
            key={t.to}
            to={t.to}
            end={t.to === "/"}
            data-testid={t.testId ?? `bottom-nav-${t.to.replace(/\//g, "")}`}
            onClick={tap}
            className={({ isActive }) =>
              `relative flex flex-col items-center gap-[5px] py-2.5 text-[11px] font-mono tracking-[0.08em] transition-colors ${
                // Canon v4 section 3 — active: weight 700 + iris-l (var(--indigo-300)) | inactive: weight 500 + ink-3.
                isActive
                  ? "text-accent-light font-bold"
                  : "text-fg-muted font-medium hover:text-fg-secondary"
              }`
            }
          >
            {({ isActive }) => (
              <>
                {/*
                  Активность несёт ВТОРОЙ признак помимо цвета — форма.
                  Замер 2026-08-07 на проде: в светлой теме активный ярлык
                  контрастнее соседей НЕ становится (4.76 против 6.84 у
                  неактивных) — иерархия перевёрнута, потому что насыщенный
                  индиго на кремовом проигрывает по яркости нейтральному
                  серому. В тёмной знак обратный (7.88 против 5.89). Один
                  приём, меняющий знак при смене темы, и оба значения при
                  этом проходят AA — тест такое не ловит.

                  Почему риска, а не подложка под иконкой. Заливка обязана
                  давать 3:1 против фона, иначе индикатора фактически нет.
                  На тёмном `#0a0c10` accent даже при 70% прозрачности даёт
                  2.29 — проходит ТОЛЬКО сплошной цвет (3.55 тёмная / 6.54
                  светлая). Сплошная пилюля потребовала бы перекрашивать
                  иконку и воевать с бейджем непрочитанных; риска даёт ту же
                  различимость двумя пикселями.
                */}
                {isActive && (
                  <span
                    aria-hidden
                    data-testid="bottom-nav-active-indicator"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-7 h-[2px] rounded-full bg-accent"
                  />
                )}
                <span className="relative">
                  <Icon active={isActive} />
                  {showBadge && (
                    <span
                      className="absolute -top-1.5 -right-2.5 min-w-[16px] h-[16px] px-1 rounded-full bg-rose-500 text-white text-[9px] font-semibold flex items-center justify-center leading-none ring-2 ring-bg-base num-display"
                      aria-label={t.badgeAriaLabel}
                    >
                      {(t.badgeCount ?? 0) > 99 ? "99+" : t.badgeCount}
                    </span>
                  )}
                </span>
                <span>{t.label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}
